import React, {useState, useEffect, useMemo} from 'react';
import {
    User, Mail, Phone, Building, MapPin, ChevronRight, ArrowLeft,
    Loader2, MessageSquare, FileText, Calendar, Moon, Sun, Eye, EyeOff, Share2Icon
} from 'lucide-react';
import {BsFillPersonVcardFill} from 'react-icons/bs';
import {useTheme} from "../ThemeContext/ThemeContext";
import useApi from "../../utils/useApi";
import constants from "../../utils/constants";
import businessIcon from "../../assets/business-contact.png";
import userIcon from "../../assets/user-contact.png";
import {BiShare} from "react-icons/bi";
import {RxShare2} from "react-icons/rx";
import {LiaSlideshare} from "react-icons/lia";
import ShareBottomSheet from "../ShareBottomSheet/ShareBottomSheet";

// Helper functions for form validation and formatting
const formatters = {
    phone: (value) => {
        // Strip non-digits
        const digits = value.replace(/\D/g, '');
        // Format as (XXX) XXX-XXXX
        if (digits.length <= 3) {
            return digits;
        } else if (digits.length <= 6) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        } else {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
        }
    }
};

// Validation rules
const validationRules = {
    email: {
        pattern: /\S+@\S+\.\S+/,
        message: 'Please enter a valid email address'
    },
    phone: {
        pattern: /^\(\d{3}\) \d{3}-\d{4}$/,
        message: 'Please enter a valid phone number'
    },
    required: {
        validate: value => !!value,
        message: 'This field is required'
    }
};

// Form selection component - OUTSIDE THE MAIN COMPONENT
const FormSelection = ({ formSelections, setFormType }) => (
    <div className="max-w-3xl mx-auto p-6 pb-0">
        <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Contact Us</h2>
        </div>

        <p className="text-gray-600 dark:text-gray-400 mb-8">
            Please select how we can help you today. We'll make sure your inquiry reaches the right team.
        </p>

        <div className="space-y-4">
            {formSelections.map(selection => (
                <button
                    key={selection.id}
                    className="w-full group flex items-center justify-between p-5 bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all dark:hover:bg-gray-700"
                    onClick={() => setFormType(selection.id)}
                >
                    <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                            <img width={45} src={selection.imgSrc}/>
                        </div>
                        <div className="ml-4 text-left">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{selection.label}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{selection.description}</p>
                        </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors"/>
                </button>
            ))}
        </div>
    </div>
);

// Render a single form field - HELPER FUNCTION
// Render a single form field - HELPER FUNCTION
const renderFormField = (field, formData, validationErrors, handleInputChange, visiblePasswords, togglePasswordVisibility) => {
    const Icon = field.icon;
    const hasValue = formData[field.id] && formData[field.id].length > 0;

    // Handle group fields - displays fields in a responsive grid layout
    if (field.type === 'group') {
        return (
            <div key={field.id} className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {field.fields.map(subField => (
                    <div key={subField.id} className="relative">
                        {renderFormField(subField, formData, validationErrors, handleInputChange, visiblePasswords, togglePasswordVisibility)}
                    </div>
                ))}
            </div>
        );
    }

    // Handle checkbox field - styled with accessible focus states and proper spacing
    if (field.type === 'checkbox') {
        return (
            <div key={field.id} className="flex items-start space-x-3 mb-5">
                <div className="flex items-center h-5">
                    <input
                        id={field.id}
                        type="checkbox"
                        name={field.id}
                        checked={formData[field.id] || false}
                        onChange={handleInputChange}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-2
                  dark:border-gray-600 dark:bg-dark-bg dark:ring-offset-gray-800 transition-colors"
                    />
                </div>
                <div className="flex flex-col">
                    <label htmlFor={field.id} className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {field.label}
                    </label>
                    {validationErrors[field.id] && (
                        <p className="text-red-500 text-xs mt-1 font-medium">{validationErrors[field.id]}</p>
                    )}
                </div>
            </div>
        );
    }

    // Handle textarea field - with floating label and focus effects
    if (field.type === 'textarea') {
        return (
            <div key={field.id} className="relative mb-5">
                <div className="relative rounded-md shadow-sm">
                    <div className="pointer-events-none absolute left-0 top-3 flex items-center pl-3">
                        {Icon && <Icon className="h-5 w-5 text-gray-400 dark:text-gray-500"/>}
                    </div>
                    <textarea
                        id={field.id}
                        name={field.id}
                        value={formData[field.id] || ''}
                        onChange={handleInputChange}
                        rows={field.rows || 4}
                        className={`block w-full !rounded-md !py-3 !pl-10 dark:!text-white !text-gray-700 !ring-1 !ring-inset
                        bg-white dark:bg-dark-bg
                        ${validationErrors[field.id]
                          ? '!ring-red-300 placeholder:text-red-300 focus:ring-red-500'
                          : '!ring-gray-300 placeholder:text-gray-400 focus:ring-indigo-600'}
                        focus:ring-2 focus:ring-inset dark:bg-dark-bg dark:text-white
                        border-2
                        focus:border-brand-purple
                        focus:border-2
                        focus:bg-white
                        focus:dark:bg-dark-bg
                        dark:ring-gray-700 dark:placeholder:text-gray-500 pt-6
                        transition-colors duration-200 ease-in-out peer
                        sm:text-sm sm:leading-6`}
                        placeholder=" "
                    />
                    <label
                        htmlFor={field.id}
                        className={`absolute text-sm duration-200 transform
                            ${hasValue ? '-translate-y-3 scale-25 top-[-12px] !text-[12px] z-10 origin-[0] bg-dark-bg dark:bg-dark-bg px-2 ml-6' : 'translate-y-0 scale-100 top-3 z-10 origin-[0] ml-10'}
                            ${validationErrors[field.id]
                              ? 'text-red-500 dark:text-red-400'
                              : 'text-gray-500 dark:text-gray-400'}
                            peer-focus:-translate-y-3
                            peer-focus:scale-25
                            peer-focus:top-[-12px]
                            peer-focus:z-10
                            peer-focus:px-2
                            peer-focus:ml-6
                            peer-focus:!my-0
                            peer-focus:bg-white
                            peer-focus:dark:bg-dark-bg
                            peer-focus:border-0
                            peer-focus:border-solid
                            peer-focus:border-brand-purple
                            peer-focus:!text-[12px]
                            rounded-tiny
                            peer-focus:text-indigo-600
                            dark:peer-focus:text-white
                            pointer-events-none
                            peer-placeholder-shown:translate-y-0
                            peer-placeholder-shown:scale-25
                            peer-placeholder-shown:top-0
                            peer-placeholder-shown:bottom-0
                            peer-placeholder-shown:my-auto
                            peer-placeholder-shown:h-fit
                            peer-placeholder-shown:ml-10
                            peer-placeholder-shown:text-gray-500
                        `}
                    >
                        {field.label}
                    </label>
                </div>
                {validationErrors[field.id] && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">{validationErrors[field.id]}</p>
                )}
            </div>
        );
    }

    // Handle select field - with custom dropdown indicator and proper styling
    if (field.type === 'select') {
        return (
            <div key={field.id} className="relative mb-5">
                <div className="relative rounded-md shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        {Icon && <Icon className="h-5 w-5 text-gray-400 dark:text-gray-500"/>}
                    </div>
                    <select
                        id={field.id}
                        name={field.id}
                        value={formData[field.id] || ''}
                        onChange={handleInputChange}
                        className={`block w-full !rounded-md !py-3 !pl-10 dark:!text-white !text-gray-700 !ring-1 !ring-inset
                        bg-white dark:bg-dark-bg
                        ${validationErrors[field.id]
                          ? '!ring-red-300 focus:ring-red-500'
                          : '!ring-gray-300 focus:ring-indigo-600'}
                        focus:ring-2 focus:ring-inset dark:bg-dark-bg dark:text-white
                        border-2
                        focus:border-brand-purple
                        focus:border-2
                        focus:bg-white
                        focus:dark:bg-dark-bg
                        dark:ring-gray-700 appearance-none bg-none pr-10 pt-6
                        transition-colors duration-200 ease-in-out peer
                        sm:text-sm sm:leading-6`}
                    >
                        {field.options.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <label
                        htmlFor={field.id}
                        className={`absolute text-sm duration-200 transform
                            ${hasValue ? '-translate-y-3 scale-25 top-[-12px] !text-[12px] z-10 origin-[0] bg-dark-bg dark:bg-dark-bg px-2 ml-6' : 'translate-y-0 scale-100 top-3 z-10 origin-[0] ml-10'}
                            ${validationErrors[field.id] ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}
                            peer-focus:-translate-y-3
                            peer-focus:scale-25
                            peer-focus:top-[-12px]
                            peer-focus:z-10
                            peer-focus:px-2
                            peer-focus:ml-6
                            peer-focus:!my-0
                            peer-focus:bg-white
                            peer-focus:dark:bg-dark-bg
                            peer-focus:border-0
                            peer-focus:border-solid
                            peer-focus:border-brand-purple
                            peer-focus:!text-[12px]
                            rounded-tiny
                            peer-focus:text-indigo-600
                            dark:peer-focus:text-white
                            pointer-events-none
                            peer-placeholder-shown:translate-y-0
                            peer-placeholder-shown:scale-25
                            peer-placeholder-shown:top-0
                            peer-placeholder-shown:bottom-0
                            peer-placeholder-shown:my-auto
                            peer-placeholder-shown:h-fit
                            peer-placeholder-shown:ml-10
                            peer-placeholder-shown:text-gray-500
                        `}
                    >
                        {field.label}
                    </label>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500 rotate-90"/>
                    </div>
                </div>
                {validationErrors[field.id] && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">{validationErrors[field.id]}</p>
                )}
            </div>
        );
    }

    // Handle password field with toggle visibility
    if (field.type === 'password') {
        return (
            <div key={field.id} className="relative mb-5">
                <div className="relative rounded-md shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        {Icon && <Icon className="h-5 w-5 text-gray-400 dark:text-gray-500"/>}
                    </div>
                    <input
                        id={field.id}
                        type={visiblePasswords[field.id] ? 'text' : 'password'}
                        name={field.id}
                        value={formData[field.id] || ''}
                        onChange={handleInputChange}
                        className={`block w-full !rounded-md !py-3 !pl-10 dark:!text-white !text-gray-700 !ring-1 !ring-inset
                        bg-white dark:bg-dark-bg
                        ${validationErrors[field.id]
                          ? '!ring-red-300 placeholder:text-red-300 focus:ring-red-500'
                          : '!ring-gray-300 placeholder:text-gray-400 focus:ring-indigo-600'}
                        focus:ring-2 focus:ring-inset dark:bg-dark-bg dark:text-white
                        border-2
                        focus:border-brand-purple
                        focus:border-2
                        focus:bg-white
                        focus:dark:bg-dark-bg
                        dark:ring-gray-700 dark:placeholder:text-gray-500 pt-6
                        transition-colors duration-200 ease-in-out peer
                        sm:text-sm sm:leading-6`}
                        placeholder=" "
                    />
                    <label
                        htmlFor={field.id}
                        className={`absolute text-sm duration-200 transform
                            ${hasValue ? '-translate-y-3 scale-25 top-[-12px] !text-[12px] z-10 origin-[0] bg-dark-bg dark:bg-dark-bg px-2 ml-6' : 'translate-y-0 scale-100 top-3 z-10 origin-[0] ml-10'}
                            ${validationErrors[field.id] ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}
                            peer-focus:-translate-y-3
                            peer-focus:scale-25
                            peer-focus:top-[-12px]
                            peer-focus:z-10
                            peer-focus:px-2
                            peer-focus:ml-6
                            peer-focus:!my-0
                            peer-focus:bg-white
                            peer-focus:dark:bg-dark-bg
                            peer-focus:border-0
                            peer-focus:border-solid
                            peer-focus:border-brand-purple
                            peer-focus:!text-[12px]
                            rounded-tiny
                            peer-focus:text-indigo-600
                            dark:peer-focus:text-white
                            pointer-events-none
                            peer-placeholder-shown:translate-y-0
                            peer-placeholder-shown:scale-25
                            peer-placeholder-shown:top-0
                            peer-placeholder-shown:bottom-0
                            peer-placeholder-shown:my-auto
                            peer-placeholder-shown:h-fit
                            peer-placeholder-shown:ml-10
                            peer-placeholder-shown:text-gray-500
                        `}
                    >
                        {field.label}
                    </label>
                    {field.showPasswordToggle && (
                        <button
                            type="button"
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            onClick={() => togglePasswordVisibility(field.id)}
                        >
                            {visiblePasswords[field.id] ? (
                                <EyeOff className="h-5 w-5"/>
                            ) : (
                                 <Eye className="h-5 w-5"/>
                             )}
                        </button>
                    )}
                </div>
                {validationErrors[field.id] && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">{validationErrors[field.id]}</p>
                )}
            </div>
        );
    }

    // Default case - render standard input field with icon and validation styling
    return (
        <div key={field.id} className="relative mb-5">
            <div className="relative rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    {Icon && <Icon className="h-5 w-5 text-gray-400 dark:text-gray-500"/>}
                </div>
                <input
                    id={field.id}
                    type={field.type}
                    name={field.id}
                    value={formData[field.id] || ''}
                    onChange={handleInputChange}
                    className={`block w-full !rounded-md !py-3 !pl-10 dark:!text-white !text-gray-700 !ring-1 !ring-inset
                    bg-white dark:bg-dark-bg
                        ${validationErrors[field.id]
                          ? '!ring-red-300 placeholder:text-red-300 focus:ring-red-500'
                          : '!ring-gray-300 placeholder:text-gray-400 focus:ring-indigo-600'}
                        focus:ring-2 focus:ring-inset dark:bg-dark-bg dark:text-white
                        border-2
                        focus:border-brand-purple
                        focus:border-2
                        focus:bg-white
                        focus:dark:bg-dark-bg
                        dark:ring-gray-700 dark:placeholder:text-gray-500 pt-6
                        transition-colors duration-200 ease-in-out peer
                        sm:text-sm sm:leading-6`}
                    placeholder=" "
                />
                <label
                    htmlFor={field.id}
                    className={`absolute text-sm duration-200 transform
                        ${hasValue || field.type == 'date' ? '-translate-y-3 scale-25 top-[-12px] !text-[12px] z-10 origin-[0] bg-dark-bg dark:bg-dark-bg px-2 ml-6' : 'translate-y-0 scale-100 top-3 z-10 origin-[0] ml-10'}
                        ${validationErrors[field.id] ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}
                        peer-focus:-translate-y-3
                        peer-focus:scale-25
                        peer-focus:top-[-12px]
                        peer-focus:z-10
                        peer-focus:px-2
                        peer-focus:ml-6
                        peer-focus:!my-0
                        peer-focus:bg-white
                        peer-focus:dark:bg-dark-bg
                        peer-focus:border-0
                        peer-focus:border-solid
                        peer-focus:border-brand-purple
                        peer-focus:!text-[12px]
                        rounded-tiny
                        peer-focus:text-indigo-600
                        dark:peer-focus:text-white
                        pointer-events-none
                        peer-placeholder-shown:translate-y-0
                        peer-placeholder-shown:scale-25
                        peer-placeholder-shown:top-0
                        peer-placeholder-shown:bottom-0
                        peer-placeholder-shown:my-auto
                        peer-placeholder-shown:h-fit
                        peer-placeholder-shown:ml-10
                        peer-placeholder-shown:text-gray-500
                    `}
                >
                    {field.label}
                </label>
            </div>
            {validationErrors[field.id] && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{validationErrors[field.id]}</p>
            )}
        </div>
    );
};

// User contact form component - OUTSIDE THE MAIN COMPONENT
const UserContactForm = ({
                             formFields,
                             formData,
                             validationErrors,
                             handleInputChange,
                             handleSubmit,
                             isSubmitting,
                             submitSuccess,
                             setFormType,
                             visiblePasswords,
                             togglePasswordVisibility
                         }) => (
    <div className="max-w-3xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
                <button
                    onClick={() => setFormType(null)}
                    className="mr-2 p-2 rounded-full bg-gray-100 dark:bg-dark-bg text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border-0 flex items-center justify-center"
                    aria-label="Go back"
                >
                    <ArrowLeft className="h-5 w-5"/>
                </button>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">User Contact Form</h2>
            </div>
        </div>

        <div className="bg-white dark:bg-dark-bg shadow-[0px_10px_30px_0px_rgba(0,0,0,0.7)] rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
            {submitSuccess ? (
                <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg flex items-center">
                    <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                    </svg>
                    <p>Thanks for reaching out! We'll get back to you shortly.</p>
                </div>
            ) : (
                 <form onSubmit={handleSubmit} className="space-y-6">
                     {formFields.userForm.map(field => renderFormField(
                         field,
                         formData,
                         validationErrors,
                         handleInputChange,
                         visiblePasswords,
                         togglePasswordVisibility
                     ))}

                     <button
                         type="submit"
                         disabled={isSubmitting}
                         className={`${
                             isSubmitting
                             ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                             : 'bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 cursor-pointer'
                         } w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors`}
                     >
                         {isSubmitting ? (
                             <div className="flex items-center">
                                 <Loader2 className="w-5 h-5 mr-2 animate-spin"/>
                                 Submitting...
                             </div>
                         ) : (
                              <span>Submit Form</span>
                          )}
                     </button>
                 </form>
             )}
        </div>
    </div>
);

// Business contact form component - OUTSIDE THE MAIN COMPONENT
const BusinessContactForm = ({
                                 formFields,
                                 formData,
                                 validationErrors,
                                 handleInputChange,
                                 handleSubmit,
                                 isSubmitting,
                                 submitSuccess,
                                 setFormType,
                                 visiblePasswords,
                                 togglePasswordVisibility
                             }) => (
    <div className="max-w-3xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
                <button
                    onClick={() => setFormType(null)}
                    className="mr-2 p-2 rounded-full bg-gray-100 dark:bg-dark-bg text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border-0 flex items-center justify-center"
                    aria-label="Go back"
                >
                    <ArrowLeft className="h-5 w-5"/>
                </button>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Advertising Contact Form</h2>
            </div>
        </div>

        <div className="bg-white dark:bg-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 shadow p-6 mb-6">
            <p className="mb-6 text-gray-600 dark:text-gray-400">
                Please complete and submit the form below. Our sales account team will contact you about your advertising inquiry within 24 hours.
            </p>

            {submitSuccess ? (
                <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg flex items-center">
                    <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                    </svg>
                    <p>Thank you for your submission! Our sales team will contact you within 24 hours.</p>
                </div>
            ) : (
                 <form onSubmit={handleSubmit} className="space-y-8">
                     {/* Render each section of the business form */}
                     {Object.entries(formFields.businessForm).map(([sectionKey, section]) => (
                         <div key={sectionKey} className={sectionKey === 'terms' ? '' : 'border-b border-gray-200 dark:border-gray-700 pb-6'}>
                             {section.title && (
                                 <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">{section.title}</h3>
                             )}

                             {/* Render section extra content if any */}
                             {section.extraContent && (
                                 <p
                                     className="text-sm mb-4 text-gray-600 dark:text-gray-400"
                                     dangerouslySetInnerHTML={{ __html: section.extraContent }}
                                 ></p>
                             )}

                             {/* Render fields in this section */}
                             <div className="space-y-4">
                                 {section.fields.map(field => renderFormField(
                                     field,
                                     formData,
                                     validationErrors,
                                     handleInputChange,
                                     visiblePasswords,
                                     togglePasswordVisibility
                                 ))}
                             </div>
                         </div>
                     ))}

                     <button
                         type="submit"
                         disabled={isSubmitting}
                         className={`${
                             isSubmitting
                             ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                             : 'bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 cursor-pointer'
                         } w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors`}
                     >
                         {isSubmitting ? (
                             <div className="flex items-center">
                                 <Loader2 className="w-5 h-5 mr-2 animate-spin"/>
                                 Submitting...
                             </div>
                         ) : (
                              <span>Submit Form</span>
                          )}
                     </button>
                 </form>
             )}
        </div>
    </div>
);

// Main Contact Component
const Contact = ({ handleShowHeader, handleShowSettingsHeader, geoposition }) => {
    // State for tracking which form to display
    const api = useApi();
    const [formType, setFormType] = useState(null); // null, 'user', or 'business'
    const { isDarkMode } = useTheme();
    const [darkMode, setDarkMode] = useState(isDarkMode);
    const [shareSheetOpen, setShareSheetOpen] = useState(false);

    // Form data state
    const [formData, setFormData] = useState({
                                                 // Contact
                                                 firstName: '',
                                                 lastName: '',
                                                 phoneNumber: '',
                                                 email: '',
                                                 businessRole: '',

                                                 // Business
                                                 businessName: '',
                                                 businessType: '',
                                                 country: '',
                                                 addressLine1: '',
                                                 addressLine2: '',
                                                 city: '',
                                                 state: '',
                                                 zipCode: '',
                                                 websiteUrl: '',
                                                 menuUrl: '',

                                                 // License
                                                 licenseNumber: '',
                                                 licenseType: '',
                                                 expirationDate: '',

                                                 // Agreement
                                                 termsAgreed: false,

                                                 // User form
                                                 message: ''
                                             });

    // Form validation state
    const [validationErrors, setValidationErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [visiblePasswords, setVisiblePasswords] = useState({});

    const handleSetFormType = (formType) => {
        setFormType(formType);
        window.scrollTo(0, 0);
    }

    useEffect(() => {
        // Set up page headers as required
        handleShowSettingsHeader(true, {showPageName: false});
        handleShowHeader(false);

        // Check system preference for dark mode
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            setDarkMode(true);
            document.documentElement.classList.add('dark');
        }
    }, [handleShowSettingsHeader, handleShowHeader]);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);

        if (searchParams.get('type') === 'user') {
            handleSetFormType('user');
        } else if (searchParams.get('type') === 'business') {
            handleSetFormType('business');
        }

        window.scrollTo(0, 0);

    }, []);

    // Form configuration objects
    const formSelections = [
        {
            id: 'user',
            label: 'I\'m a User',
            icon: User,
            description: 'Questions, feedback, or support requests',
            imgSrc: userIcon
        },
        {
            id: 'business',
            label: 'I\'m a Dispensary or Brand',
            icon: Building,
            description: 'Partnership, advertising, or business inquiries',
            imgSrc: businessIcon

        }
    ];

    // Define form fields using useMemo to avoid recreation on every render
    const formFields = useMemo(() => {
        return {
            userForm: [
                {
                    id: 'firstName',
                    label: 'Name',
                    type: 'text',
                    placeholder: 'First Name',
                    icon: User,
                    required: true,
                    errorMessage: 'First name is required',
                    validator: 'required'
                },
                {
                    id: 'lastName',
                    label: 'Last Name',
                    type: 'text',
                    placeholder: 'Last Name',
                    icon: BsFillPersonVcardFill,
                    required: true,
                    errorMessage: 'Last name is required',
                    validator: 'required'
                },
                {
                    id: 'email',
                    label: 'Email',
                    type: 'email',
                    placeholder: 'Email Address',
                    icon: Mail,
                    required: true,
                    errorMessage: 'Email is required',
                    validator: 'email'
                },
                {
                    id: 'phoneNumber',
                    label: 'Phone',
                    type: 'tel',
                    placeholder: 'Phone Number',
                    icon: Phone,
                    formatter: 'phone',
                    validator: 'phone'
                },
                {
                    id: 'message',
                    label: 'Message',
                    type: 'textarea',
                    placeholder: 'How can we help you?',
                    icon: MessageSquare,
                    rows: 4
                }
            ],
            businessForm: {
                contact: {
                    title: 'Contact',
                    fields: [
                        {
                            id: 'firstName',
                            label: 'First Name',
                            type: 'text',
                            placeholder: 'First Name',
                            icon: User,
                            required: true,
                            errorMessage: 'First name is required',
                            validator: 'required'
                        },
                        {
                            id: 'lastName',
                            label: 'Last Name',
                            type: 'text',
                            placeholder: 'Last Name',
                            icon: BsFillPersonVcardFill,
                            required: true,
                            errorMessage: 'Last name is required',
                            validator: 'required'
                        },
                        {
                            id: 'phoneNumber',
                            label: 'Phone',
                            type: 'tel',
                            placeholder: 'Phone Number',
                            icon: Phone,
                            formatter: 'phone',
                            validator: 'phone'
                        },
                        {
                            id: 'email',
                            label: 'Email',
                            type: 'email',
                            placeholder: 'Email Address',
                            icon: Mail,
                            required: true,
                            errorMessage: 'Email is required',
                            validator: 'email'
                        },
                        {
                            id: 'businessRole',
                            label: 'Business Role',
                            type: 'select',
                            icon: User,
                            options: [
                                { value: '', label: '' },
                                { value: 'Marketing Manager', label: 'Marketing Manager' },
                                { value: 'Manager', label: 'Manager' },
                                { value: 'Owner', label: 'Owner' },
                                { value: 'Ad Agency', label: 'Ad Agency' },
                                { value: 'Other', label: 'Other' }
                            ]
                        }
                    ]
                },
                business: {
                    title: 'Business',
                    fields: [
                        {
                            id: 'businessName',
                            label: 'Business Name',
                            type: 'text',
                            placeholder: 'Business Name',
                            icon: Building,
                            required: true,
                            errorMessage: 'Business name is required',
                            validator: 'required'
                        },
                        {
                            id: 'businessType',
                            label: 'Business Type',
                            type: 'select',
                            icon: Building,
                            required: true,
                            errorMessage: 'Business type is required',
                            validator: 'required',
                            options: [
                                { value: '', label: '' },
                                { value: 'Dispensary', label: 'Dispensary' },
                                { value: 'Brand', label: 'Brand' },
                                { value: 'Delivery', label: 'Delivery' },
                                { value: 'Other', label: 'Other' }
                            ]
                        },
                        {
                            id: 'country',
                            label: 'Country',
                            type: 'text',
                            placeholder: 'Country',
                            icon: MapPin
                        },
                        {
                            id: 'addressLine1',
                            label: 'Address Line 1',
                            type: 'text',
                            placeholder: 'Address Line 1',
                            icon: MapPin
                        },
                        {
                            id: 'addressLine2',
                            label: 'Address Line 2',
                            type: 'text',
                            placeholder: 'Address Line 2',
                            icon: MapPin
                        },
                        {
                            id: 'cityStateZip',
                            type: 'group',
                            fields: [
                                {
                                    id: 'city',
                                    label: 'City',
                                    type: 'text',
                                    placeholder: 'City',
                                    icon: MapPin
                                },
                                {
                                    id: 'state',
                                    label: 'State',
                                    type: 'text',
                                    placeholder: 'State',
                                    icon: MapPin
                                },
                                {
                                    id: 'zipCode',
                                    label: 'Zip Code',
                                    type: 'text',
                                    placeholder: 'Zip Code',
                                    icon: MapPin
                                }
                            ]
                        },
                        {
                            id: 'websiteUrl',
                            label: 'Website URL',
                            type: 'url',
                            placeholder: 'Website URL',
                            icon: FileText
                        },
                        {
                            id: 'menuUrl',
                            label: 'Menu URL',
                            type: 'url',
                            placeholder: 'Menu URL',
                            icon: FileText
                        }
                    ]
                },
                license: {
                    title: 'License Information',
                    fields: [
                        {
                            id: 'licenseGroup',
                            type: 'group',
                            fields: [
                                {
                                    id: 'licenseNumber',
                                    label: 'License Number',
                                    type: 'text',
                                    placeholder: 'License Number',
                                    icon: FileText
                                },
                                {
                                    id: 'licenseType',
                                    label: 'License Type',
                                    type: 'select',
                                    icon: FileText,
                                    options: [
                                        { value: '', label: '' },
                                        { value: 'Retail', label: 'Retail' },
                                        { value: 'Cultivation', label: 'Cultivation' },
                                        { value: 'Wholesale', label: 'Wholesale' }
                                    ]
                                },
                                {
                                    id: 'expirationDate',
                                    label: 'Expiration (mm/dd/yyyy)',
                                    type: 'date',
                                    icon: Calendar
                                }
                            ]
                        }
                    ]
                },
                terms: {
                    title: '',
                    fields: [
                        {
                            id: 'termsAgreed',
                            type: 'checkbox',
                            label: 'I have read and agree to the terms and conditions stated above.',
                            required: true,
                            errorMessage: 'You must agree to the terms',
                            validator: 'required'
                        }
                    ],
                    extraContent: `By completing this form, you agree that we may process your information in accordance with our <a href="${constants.PRIVACY_URL}" class="text-[var(--branding-green)] hover:underline">Privacy Policy</a>. You acknowledge that all use of the platform for business is subject to the <a href="${constants.TOS_URL}" class="!text-[var(--branding-green)] hover:underline">Terms of Use</a> and the <a href="${constants.PRIVACY_URL}" class="!text-[var(--branding-green)] hover:underline">Privacy Policy</a>, and you agree to be bound by these Terms and any applicable additional terms. You agree to receive all communications electronically.`
                }
            }
        };
    }, []);

    // Create a default product object if none is provided
    const shareProduct = {
        id: null,
        name: "pluug",
        brand_name: "",
        dispensary: { name: "" },
        pricing: [{ price: 0, discount_price: 0, unit: "" }]
    };

    // Function to get field definition
    const getFieldDefinition = (name) => {
        // Search in user form
        let field = formFields.userForm.find(f => f.id === name);
        if (field) return field;

        // Search in business form
        for (const section in formFields.businessForm) {
            const fields = formFields.businessForm[section].fields;
            if (Array.isArray(fields)) {
                field = fields.find(f => f.id === name);
                if (field) return field;

                // Check for group fields
                for (const groupField of fields) {
                    if (groupField.type === 'group' && Array.isArray(groupField.fields)) {
                        field = groupField.fields.find(f => f.id === name);
                        if (field) return field;
                    }
                }
            }
        }

        return null;
    };

    // Function to validate a single field
    const validateField = (name, value, fieldDef) => {
        // Clear existing errors for this field
        let newErrors = { ...validationErrors };
        delete newErrors[name];

        // Required field validation
        if (fieldDef?.required && !value) {
            newErrors[name] = fieldDef.errorMessage || 'This field is required';
        }

        // Pattern validation based on validator type
        if (fieldDef?.validator && validationRules[fieldDef.validator]) {
            const rule = validationRules[fieldDef.validator];
            if (value && rule.pattern && !rule.pattern.test(value)) {
                newErrors[name] = rule.message;
            }
        }

        setValidationErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form field changes - Based on ClientForm pattern
    const handleInputChange = (e) => {
        const { name, value, type, checked, selectionStart } = e.target;

        // Find field definition
        const fieldDef = getFieldDefinition(name);

        // Handle different value types based on input type
        let newValue = type === 'checkbox' ? checked : value;
        let cursorPosition = selectionStart;

        // Apply formatter if defined
        if (fieldDef?.formatter && formatters[fieldDef.formatter]) {
            const previousValue = formData[name] || '';
            const previousLength = previousValue.length;

            newValue = formatters[fieldDef.formatter](value);

            // Adjust cursor position after formatting
            if (cursorPosition) {
                // Logic for cursor positioning after formatting
                if (newValue.length < previousLength) {
                    // Handle deletion
                    if (previousValue[cursorPosition - 1] === '-' ||
                        previousValue[cursorPosition - 1] === ')' ||
                        previousValue[cursorPosition - 1] === ' ') {
                        cursorPosition--;
                    }
                } else if (newValue.length > previousLength) {
                    // Handle insertion with special characters
                    if (newValue[cursorPosition] === '-' ||
                        newValue[cursorPosition] === ')' ||
                        newValue[cursorPosition] === ' ' ||
                        newValue[cursorPosition - 1] === '(') {
                        cursorPosition++;
                    }
                }

                // Set cursor position after render
                setTimeout(() => {
                    const input = document.getElementById(name);
                    if (input && typeof input.setSelectionRange === 'function') {
                        input.setSelectionRange(cursorPosition, cursorPosition);
                    }
                }, 0);
            }
        }

        // Update form data state
        setFormData(prevData => ({
            ...prevData,
            [name]: newValue
        }));

        // Validate the field
        validateField(name, newValue, fieldDef);
    };

    const handleShare = () => {
        setShareSheetOpen(true);
    }

    // Form validation for submission
    const validateForm = () => {
        const newErrors = {};
        const fieldsToValidate = formType === 'user'
                                 ? formFields.userForm
                                 : Object.values(formFields.businessForm).flatMap(section =>
                                                                                      Array.isArray(section.fields)
                                                                                      ? section.fields.flatMap(field =>
                                                                                                                   field.type === 'group' ? field.fields : field
                                                                                      )
                                                                                      : []
            );

        // Validate each field
        fieldsToValidate.forEach(field => {
            // Skip group fields
            if (field.type === 'group') return;

            // Check required fields
            if (field.required && !formData[field.id]) {
                newErrors[field.id] = field.errorMessage || `${field.label} is required`;
            }

            // Apply pattern validation
            if (field.validator && validationRules[field.validator]) {
                const rule = validationRules[field.validator];
                if (formData[field.id] && rule.pattern && !rule.pattern.test(formData[field.id])) {
                    newErrors[field.id] = rule.message;
                }
            }
        });

        setValidationErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (validateForm()) {
            setIsSubmitting(true);

            try {
                // Call the appropriate API endpoint based on the form type
                const response = formType === 'user'
                                 ? await api.submitUserContactForm(formData)
                                 : await api.submitBusinessContactForm(formData);

                console.log('Form submitted:', response);
                setSubmitSuccess(true);

                // Reset form after successful submission
                setTimeout(() => {
                    setFormType(null);
                    setFormData({
                                    firstName: '',
                                    lastName: '',
                                    phoneNumber: '',
                                    email: '',
                                    businessRole: '',
                                    businessName: '',
                                    businessType: '',
                                    country: '',
                                    addressLine1: '',
                                    addressLine2: '',
                                    city: '',
                                    state: '',
                                    zipCode: '',
                                    websiteUrl: '',
                                    menuUrl: '',
                                    licenseNumber: '',
                                    licenseType: '',
                                    expirationDate: '',
                                    termsAgreed: false,
                                    message: ''
                                });
                    setSubmitSuccess(false);
                }, 3000);
            }
            catch (error) {
                console.error('Error submitting form:', error);

                // Check if there are validation errors from the server
                if (error.response && error.response.data && error.response.data.errors) {
                    setValidationErrors(error.response.data.errors);
                } else {
                    // Set a generic error message
                    setError('An error occurred while submitting the form. Please try again.');
                }
            }
            finally {
                setIsSubmitting(false);
            }
        }
    };

    // Toggle password visibility for password fields
    const togglePasswordVisibility = (fieldId) => {
        setVisiblePasswords(prev => ({
            ...prev,
            [fieldId]: !prev[fieldId]
        }));
    };

    // Render the appropriate form based on user selection
    return (
        <div className={`min-h-screen pb-[100px]  transition-colors duration-200`}>
            {formType === null && (
                <>
                    <FormSelection
                        formSelections={formSelections}
                        setFormType={handleSetFormType}
                    />
                    <div className="max-w-3xl mx-auto px-6 py-5">
                        <div className="space-y-4">

                            <button
                                className="w-full group flex items-center justify-between p-5 bg-white dark:bg-brand-purple rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all dark:hover:bg-gray-700"
                                onClick={handleShare}
                            >
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 h-12 w-12 rounded-xs bg-indigo-100 dark:bg-black flex items-center justify-center">
                                        <LiaSlideshare size={27} className="text-white " />
                                    </div>
                                    <div className="ml-4 text-left">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Refer your friends</h3>
                                        <p className="text-sm text-gray-500 dark:text-white">Earn up to $100 when you share pluug and you and they both sign up!</p>
                                    </div>
                                </div>
                                <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors"/>
                            </button>

                        </div>
                    </div>
                </>
            )}

            {
                formType === 'user' && (
                             <UserContactForm
                                 formFields={formFields}
                                 formData={formData}
                                 validationErrors={validationErrors}
                                 handleInputChange={handleInputChange}
                                 handleSubmit={handleSubmit}
                                 isSubmitting={isSubmitting}
                                 submitSuccess={submitSuccess}
                                 setFormType={setFormType}
                                 visiblePasswords={visiblePasswords}
                                 togglePasswordVisibility={togglePasswordVisibility}
                             />
                         )
            }

            {
                formType === 'business' && (
                             <BusinessContactForm
                                 formFields={formFields}
                                 formData={formData}
                                 validationErrors={validationErrors}
                                 handleInputChange={handleInputChange}
                                 handleSubmit={handleSubmit}
                                 isSubmitting={isSubmitting}
                                 submitSuccess={submitSuccess}
                                 setFormType={setFormType}
                                 visiblePasswords={visiblePasswords}
                                 togglePasswordVisibility={togglePasswordVisibility}
                             />
                         )}

            {/* Render ShareBottomSheet only when open */}
            {shareSheetOpen && (
                <ShareBottomSheet
                    product={shareProduct}
                    open={shareSheetOpen}
                    onOpenChange={setShareSheetOpen}
                />
            )}
        </div>
    );
};

export default Contact;
